import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Grid,
  LinearProgress,
  Alert,
  Chip,
  Divider,
  Paper,
  Stack,
} from '@mui/material';
import {
  CheckCircle as CheckIcon,
  Warning as WarningIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { OcrResult } from '../Hooks/useOcrProcessing';
import { UserInvoiceCorrections } from '../Types/userInvoiceCorrections';

interface OcrPreviewModalProps {
  open: boolean;
  onClose: () => void;
  ocrResult: OcrResult | null;
  file: File | null;
  onConfirm: (corrections: UserInvoiceCorrections) => void;
  onSkipOcr: () => void;
}

/**
 * Modal for previewing OCR results and allowing user corrections
 * Displays extracted invoice data with confidence score
 * User can edit fields before submitting to backend
 */
const OcrPreviewModal: React.FC<OcrPreviewModalProps> = ({
  open,
  onClose,
  ocrResult,
  file,
  onConfirm,
  onSkipOcr,
}) => {
  const [corrections, setCorrections] = useState<UserInvoiceCorrections>({});

  const handleFieldChange = (field: keyof UserInvoiceCorrections, value: string) => {
    setCorrections((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleConfirm = () => {
    // Filter out empty corrections
    const finalCorrections = Object.entries(corrections).reduce((acc, [key, value]) => {
      if (value && value.toString().trim() !== '') {
        acc[key as keyof UserInvoiceCorrections] = value;
      }
      return acc;
    }, {} as UserInvoiceCorrections);

    onConfirm(finalCorrections);
  };

  const handleSkip = () => {
    onSkipOcr();
  };

  if (!ocrResult || !file) return null;

  const { confidence, extractedData } = ocrResult;
  const isLowConfidence = confidence < 70;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">Podgląd OCR - {file.name}</Typography>
          <Button onClick={onClose} size="small" startIcon={<CloseIcon />}>
            Anuluj
          </Button>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        <Stack spacing={3}>
          {/* Confidence Score */}
          <Paper sx={{ p: 2, bgcolor: isLowConfidence ? 'warning.light' : 'success.light' }}>
            <Box display="flex" alignItems="center" gap={1} mb={1}>
              {isLowConfidence ? <WarningIcon color="warning" /> : <CheckIcon color="success" />}
              <Typography variant="subtitle1" fontWeight="medium">
                Jakość rozpoznania: {confidence.toFixed(1)}%
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={confidence}
              color={isLowConfidence ? 'warning' : 'success'}
              sx={{ height: 8, borderRadius: 1 }}
            />
            {isLowConfidence && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                Niska jakość rozpoznania - sprawdź i popraw dane przed wysłaniem
              </Typography>
            )}
          </Paper>

          {/* Info Alert */}
          <Alert severity="info">
            Sprawdź wyodrębnione dane z faktury. Korekty są stosowane na etapie weryfikacji i
            zatwierdzenia importu (nie podczas samego uploadu pliku).
          </Alert>

          {/* Extracted Fields */}
          <Box>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Wyodrębnione dane faktury
            </Typography>
            <Divider sx={{ mb: 2 }} />

            <Grid container spacing={2}>
              {/* Invoice Number */}
              <Grid size={{ xs: 12, md: 6 }} >
                <TextField
                  fullWidth
                  label="Numer faktury"
                  placeholder="np. FV/2025/001"
                  defaultValue={extractedData.invoiceNumber || ''}
                  onChange={(e) => handleFieldChange('invoiceNumber', e.target.value)}
                  helperText={
                    extractedData.invoiceNumber
                      ? 'OCR wykrył numer faktury'
                      : 'OCR nie wykrył - wprowadź ręcznie'
                  }
                  InputProps={{
                    startAdornment: extractedData.invoiceNumber && (
                      <Chip label="OCR" size="small" color="primary" sx={{ mr: 1 }} />
                    ),
                  }}
                />
              </Grid>

              {/* Issue Date */}
              <Grid size={{ xs: 12, md: 6 }} >
                <TextField
                  fullWidth
                  label="Data wystawienia"
                  type="date"
                  InputLabelProps={{ shrink: true }}
                  defaultValue={
                    extractedData.issueDate
                      ? extractedData.issueDate.split('-').reverse().join('-')
                      : ''
                  }
                  onChange={(e) => handleFieldChange('issueDate', e.target.value)}
                  helperText={
                    extractedData.issueDate
                      ? 'OCR wykrył datę'
                      : 'OCR nie wykrył - wprowadź ręcznie'
                  }
                />
              </Grid>

              {/* Total Gross */}
              <Grid size={{ xs: 12, md: 6 }} >
                <TextField
                  fullWidth
                  label="Kwota brutto"
                  type="number"
                  placeholder="np. 1230.50"
                  defaultValue={extractedData.totalGross || ''}
                  onChange={(e) => handleFieldChange('totalGross', e.target.value)}
                  helperText={
                    extractedData.totalGross
                      ? 'OCR wykrył kwotę'
                      : 'OCR nie wykrył - wprowadź ręcznie'
                  }
                  InputProps={{
                    startAdornment: extractedData.totalGross && (
                      <Chip label="OCR" size="small" color="primary" sx={{ mr: 1 }} />
                    ),
                  }}
                />
              </Grid>

              {/* Currency */}
              <Grid size={{ xs: 12, md: 6 }} >
                <TextField
                  fullWidth
                  label="Waluta"
                  placeholder="np. PLN, EUR, USD"
                  defaultValue=""
                  onChange={(e) => handleFieldChange('currency', e.target.value)}
                  helperText="Kod waluty (ISO 4217)"
                />
              </Grid>

              {/* Seller Name */}
              <Grid size={{ xs: 12, md: 6 }} >
                <TextField
                  fullWidth
                  label="Nazwa sprzedawcy"
                  defaultValue={extractedData.sellerName || ''}
                  onChange={(e) => handleFieldChange('sellerName', e.target.value)}
                  helperText={
                    extractedData.sellerName
                      ? 'OCR wykrył sprzedawcę'
                      : 'OCR nie wykrył - wprowadź ręcznie'
                  }
                  InputProps={{
                    startAdornment: extractedData.sellerName && (
                      <Chip label="OCR" size="small" color="primary" sx={{ mr: 1 }} />
                    ),
                  }}
                />
              </Grid>

              {/* Seller NIP */}
              <Grid size={{ xs: 12, md: 6 }} >
                <TextField
                  fullWidth
                  label="NIP sprzedawcy"
                  placeholder="np. 1234567890"
                  onChange={(e) => handleFieldChange('sellerNip', e.target.value)}
                  helperText="Numer identyfikacji podatkowej sprzedawcy"
                />
              </Grid>

              {/* Buyer Name */}
              <Grid size={{ xs: 12, md: 6 }} >
                <TextField
                  fullWidth
                  label="Nazwa nabywcy"
                  defaultValue={extractedData.buyerName || ''}
                  onChange={(e) => handleFieldChange('buyerName', e.target.value)}
                  helperText={
                    extractedData.buyerName
                      ? 'OCR wykrył nabywcę'
                      : 'OCR nie wykrył - wprowadź ręcznie'
                  }
                  InputProps={{
                    startAdornment: extractedData.buyerName && (
                      <Chip label="OCR" size="small" color="primary" sx={{ mr: 1 }} />
                    ),
                  }}
                />
              </Grid>

              {/* Buyer NIP */}
              <Grid size={{ xs: 12, md: 6 }} >
                <TextField
                  fullWidth
                  label="NIP nabywcy"
                  placeholder="np. 9876543210"
                  onChange={(e) => handleFieldChange('buyerNip', e.target.value)}
                  helperText="Numer identyfikacji podatkowej nabywcy"
                />
              </Grid>

              {/* Notes */}
              <Grid size={{ xs: 12 }} >
                <TextField
                  fullWidth
                  label="Notatki (opcjonalnie)"
                  multiline
                  rows={2}
                  placeholder="Dodatkowe uwagi o korektach..."
                  onChange={(e) => handleFieldChange('notes', e.target.value)}
                  helperText="Opcjonalne notatki o wprowadzonych korektach"
                />
              </Grid>
            </Grid>
          </Box>

          {/* Warning for low confidence */}
          {isLowConfidence && (
            <Alert severity="warning">
              <Typography variant="body2" fontWeight="medium" gutterBottom>
                Niska jakość rozpoznania tekstu
              </Typography>
              <Typography variant="body2">
                Zalecamy dokładne sprawdzenie wszystkich pól. Możesz też pominąć OCR i wysłać plik
                bezpośrednio do backendu (backend użyje własnego OCR).
              </Typography>
            </Alert>
          )}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={handleSkip} variant="outlined" color="secondary">
          Pomiń OCR (wyślij bezpośrednio)
        </Button>
        <Button onClick={handleConfirm} variant="contained" color="primary" autoFocus>
          Potwierdź i wyślij
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default OcrPreviewModal;
